import type { Option, RowLocalId } from "@tento-lona/sheets";
import type { ClientLifecycle } from "../client/handle-errors.ts";
import type { PreferencesStorage } from "./preferences-storage.ts";
import { Actor, type InvalidationSink, LayoutChanged } from "../invalidation.ts";
import { StoredPreference } from "./stored-preference.ts";

const STORAGE_KEY = "lona-pref-row-heights";

/**
 * Per-row height overrides, stored locally (`client.preferences.rowHeight()`)
 * so a user can size a row without write access to it: `peek(row)` reads
 * one, `set(row, heightPx, actor)` stores it.
 */
export class RowHeightPreferences {
  static readonly ACTOR = new Actor("row-height-preferences");
  readonly #storage: PreferencesStorage;
  readonly #invalidation: InvalidationSink;
  #heightMap: Option<Map<string, number>> = null;
  /** The owning client's liveness: `set` checks it first. */
  readonly #lifecycle: ClientLifecycle;

  /** `lifecycle`: the owning client's (a standalone face: `new LonaSdk.Client.Lifecycle()`). */
  constructor(storage: PreferencesStorage, invalidation: InvalidationSink, lifecycle: ClientLifecycle) {
    this.#storage = storage;
    this.#invalidation = invalidation;
    this.#lifecycle = lifecycle;
  }

  /** `row`'s stored height override (keyed by its current wire key); `null` when none. */
  peek(row: RowLocalId): Option<number> {
    return this.#loadMap().get(row.toString()) ?? null;
  }

  /** Stores `row`'s height override (`null` clears it) and emits `LayoutChanged` as `actor`. */
  set(row: RowLocalId, heightPx: Option<number>, actor: Actor): void {
    this.#lifecycle.assertLive("preferences.rowHeight.set");
    const map = this.#loadMap();
    if (heightPx == null) {
      map.delete(row.toString());
    } else {
      map.set(row.toString(), heightPx);
    }
    this.#saveMap(map);
    this.#invalidation.emit(new LayoutChanged(actor));
  }

  /**
   * Apply stored heights to a registry's rows (lona-js path): a
   * `SheetRow.Registry`, or anything that lists rows with their id and wire
   * fields.
   */
  applyToRegistry(registry: { iter(): Iterable<readonly [RowLocalId, { readonly id: RowLocalId; readonly raw: object }]> }): void {
    const map = this.#loadMap();
    for (const [_, row] of registry.iter()) {
      const h = map.get(row.id.toString());
      if (h != null) {
        (row.raw as { heightPx?: Option<number> }).heightPx = h;
      }
    }
  }

  /**
   * Migrate stored heights from old wire keys to new ones, in the
   * in-memory map AND the persisted record.
   *
   * Called from the client's promote handler (which owns the id
   * remap): an ephemeral row's server promote re-keys its wire id, and
   * this store keys by the WIRE string — so a saved height would
   * silently orphan under the stale ephemeral key. Each `old → new`
   * entry is moved; keys absent from the map are no-ops, and post-promote
   * server UUIDs never change again. Persistence bars `serial`, so the
   * wire key stays the store key and we migrate entries on rekey instead.
   */
  migrateKeys(remap: ReadonlyMap<string, string>): void {
    const map = this.#loadMap();
    const moves: [string, number][] = [];
    for (const [from, to] of remap) {
      if (from === to) continue;
      const h = map.get(from);
      if (h != null) moves.push([to, h]);
    }
    if (moves.length === 0) return;
    for (const [from, to] of remap) {
      if (from !== to) map.delete(from);
    }
    for (const [to, h] of moves) map.set(to, h);
    this.#saveMap(map);
  }

  #loadMap(): Map<string, number> {
    if (!this.#heightMap) {
      this.#heightMap = new Map();
      // Unusable storage is logged and reads as "no overrides".
      const raw = StoredPreference.record(
        STORAGE_KEY,
        StoredPreference.json(this.#storage, STORAGE_KEY),
      );
      for (const [rowId, value] of Object.entries(raw ?? {})) {
        const height = StoredPreference.finite(STORAGE_KEY, value);
        if (height != null) this.#heightMap.set(rowId, height);
      }
    }
    return this.#heightMap;
  }

  #saveMap(map: Map<string, number>): void {
    this.#heightMap = map;
    const raw: Record<string, number> = {};
    for (const [k, v] of map) {
      raw[k] = v;
    }
    this.#storage.setItem(STORAGE_KEY, JSON.stringify(raw));
  }
}
